import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode
} from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { setApiRegion } from '../utils/jarvisApiClient';
import {
  getRegionSwitchPath,
  isRegionSwitchStayPath
} from '../utils/regionNavigation';

interface Region {
  code: string;
  fullName: string;
  flagName: string;
}

interface RegionContextType {
  region: Region;
  setRegion: (region: string) => void;
  availableRegions: Region[];
}

const RegionContext = createContext<RegionContextType>({
  region: { code: 'US', fullName: 'USA', flagName: 'usa' },
  setRegion: () => {},
  availableRegions: []
});

const availableRegions = {
  US: { code: 'US', fullName: 'USA', flagName: 'usa' },
  EU: { code: 'EU', fullName: 'Europe', flagName: 'europe' },
  IN: { code: 'IN', fullName: 'India', flagName: 'india' },
  AU: { code: 'AU', fullName: 'Australia', flagName: 'australia' },
  CA: { code: 'CA', fullName: 'Canada', flagName: 'canada' }
};

export const RegionProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const history = useHistory();
  const [region, setRegionState] = useState(availableRegions.US);

  useEffect(() => {
    setApiRegion(region.code);
  }, [region.code]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlRegion = params.get('jarvisRegion');

    if (urlRegion && urlRegion !== region.code) {
      const matchedRegion = Object.values(availableRegions).find(
        r => r.code === urlRegion
      );
      if (matchedRegion) {
        setRegionState(matchedRegion);
        setApiRegion(matchedRegion.code);
      } else {
        history.replace(`/invalid-region?code=${urlRegion}`);
        return;
      }
    } else if (!urlRegion) {
      // Ensure we only use the pathname part, not including any query parameters
      const cleanPathname = location.pathname.split('?')[0];
      params.set('jarvisRegion', region.code);
      history.replace(`${cleanPathname}?${params.toString()}`, location.state);
    }
  }, [
    location.search,
    location.state,
    region.code,
    history,
    location.pathname
  ]);

  const setRegion = (newRegion: string) => {
    if (newRegion === region.code) return;

    const matchedRegion = Object.values(availableRegions).find(
      r => r.code === newRegion
    );
    if (!matchedRegion) {
      console.error(`Attempted to set invalid region: ${newRegion}`);
      history.replace(`/invalid-region?code=${newRegion}`);
      return;
    }

    // Point the API client at the new region before anything navigates, so the
    // remounted page's first requests already carry the new region header.
    setApiRegion(newRegion);

    // Ensure we only use the pathname part, not including any query parameters
    const cleanPathname = location.pathname.split('?')[0];
    // Keep the user on their page; collapse region-specific detail pages to
    // their section list (their entity id is invalid in the new region).
    const targetPathname = getRegionSwitchPath(cleanPathname);
    // Preserve the current query params (filters, table config) only when
    // staying on the same page. When collapsing a detail page to its section
    // list, those params belong to the source page and would break the list
    // page, so start fresh and carry over only the region.
    const stayOnSamePage = targetPathname === cleanPathname;
    const params = new URLSearchParams(stayOnSamePage ? location.search : '');
    params.set('jarvisRegion', newRegion);
    // Stay-put exceptions keep their router state; the page re-resolves its
    // entity in the new region. Everything else starts clean.
    history.push(
      `${targetPathname}?${params.toString()}`,
      isRegionSwitchStayPath(cleanPathname) ? location.state : undefined
    );
    setRegionState(matchedRegion);
  };

  return (
    <RegionContext.Provider
      value={{
        region,
        setRegion,
        availableRegions: Object.values(availableRegions)
      }}
    >
      {children}
    </RegionContext.Provider>
  );
};

export const useRegion = () => useContext(RegionContext);
